import { defineStore } from 'pinia';
import authService from '../services/authService';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('jwt_token') || null,
    isAuthenticated: !!localStorage.getItem('jwt_token'),
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoggedIn: (state) => state.isAuthenticated,
    userRoles: (state) => state.user?.roles || [],
    userPermissions: (state) => state.user?.permissions || [],
  },

  actions: {
    async login(email, password) {
      try {
        const response = await authService.login(email, password);
        
        // Salva token e user
        this.token = response.token || response.access_token;
        this.isAuthenticated = true;
        
        localStorage.setItem('jwt_token', this.token);
        
        // Ottieni informazioni utente
        await this.fetchUser();
        
        return { success: true };
      } catch (error) {
        console.error('Login error:', error);
        const errorMessage = error.response?.data?.messages?.error || error.response?.data?.message || 'Errore durante il login';
        
        return {
          success: false,
          message: errorMessage,
        };
      }
    },

    async fetchUser() {
      try {
        const userData = await authService.me();
        this.user = userData;
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        // ⚠️ IMPORTANT: Don't clear the token if we still have it in localStorage
        // The token might be valid, but the /me endpoint might be failing for other reasons
        // (timeout, backend issue, permission issue, etc)
        
        const token = localStorage.getItem('jwt_token');
        if (token) {
          // Token still exists - DON'T clear it!
          // Keep the user null, which will prevent app access until token is actually invalid
          this.user = null;
          this.isAuthenticated = false;
          localStorage.removeItem('user');
          // Token stays in localStorage for retry
        } else {
          // No token at all - clear everything
          this.user = null;
          this.token = null;
          this.isAuthenticated = false;
          localStorage.removeItem('user');
        }
      }
    },

    async logout() {
      try {
        if (this.token) {
          await authService.logout();
        }
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        // Pulisci sempre lo stato locale
        this.user = null;
        this.token = null;
        this.isAuthenticated = false;
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
      }
    },

    // Verifica se l'utente ha un determinato ruolo
    hasRole(role) {
      return this.userRoles.includes(role);
    },

    // Verifica se l'utente è admin
    isAdmin() {
      return this.hasRole('admin');
    },

    // Verifica se l'utente ha una determinata permissione
    hasPermission(permission) {
      return this.userPermissions.includes(permission);
    },

    // Verifica se l'utente ha una delle permissioni indicate
    hasAnyPermission(permissions) {
      if (!Array.isArray(permissions)) permissions = [permissions];
      return permissions.some(p => this.userPermissions.includes(p));
    },

    // Verifica se l'utente ha tutte le permissioni indicate
    hasAllPermissions(permissions) {
      if (!Array.isArray(permissions)) permissions = [permissions];
      return permissions.every(p => this.userPermissions.includes(p));
    },
  },
});
